/* eslint-disable react/jsx-key */
import { Text, Box, Flex, HStack, Table, useBreakpointValue } from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { KeyboardEvent, MouseEvent, PropsWithChildren, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
    aggregationFn_extent,
    aggregationFn_sum,
    CellContext,
    columnFilteringFeature,
    columnGroupingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    ColumnDef,
    ColumnVisibilityState,
    createExpandedRowModel,
    createFilteredRowModel,
    createGroupedRowModel,
    createSortedRowModel,
    FilterFn,
    flexRender,
    globalFilteringFeature,
    GroupingState,
    HeaderGroup,
    PaginationState,
    Row,
    rowAggregationFeature,
    rowExpandingFeature,
    rowSortingFeature,
    sortFn_alphanumeric,
    sortFn_datetime,
    sortFn_text,
    SortingState,
    tableFeatures,
    useTable,
} from '@tanstack/react-table'
import { FcList, FcTreeStructure } from "react-icons/fc";
import { BiMinusCircle, BiPlusCircle, BiSolidSortAlt, BiSortDown, BiSortUp, BiChevronsDown } from 'react-icons/bi';
import { AmountUtil } from 'src/data/AmountUtil';
import { formatOverview } from 'src/data/DateTimeUtil';
import { Money } from 'src/api/Money';
import { useColorModeValue } from './ui/color-mode';
import { useVirtualizer } from '@tanstack/react-virtual'
import ExplanationPopover from './ExplanationPopover';
import TablePagination from './TablePagination';
import { useI18n } from 'src/i18n/I18nContext';

const dataTableFeatures = tableFeatures({
    columnFilteringFeature,
    columnGroupingFeature,
    columnSizingFeature,
    columnVisibilityFeature,
    globalFilteringFeature,
    rowAggregationFeature,
    rowExpandingFeature,
    rowSortingFeature,
    aggregationFns: {
        extent: aggregationFn_extent,
        sum: aggregationFn_sum,
    },
    expandedRowModel: createExpandedRowModel(),
    filteredRowModel: createFilteredRowModel(),
    groupedRowModel: createGroupedRowModel(),
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        datetime: sortFn_datetime,
        text: sortFn_text,
    },
});

export type DataTableFeatures = typeof dataTableFeatures;
export type DataTableColumnDef<T extends object, TValue = unknown> = ColumnDef<DataTableFeatures, T, TValue>;
export type DataTableCellContext<T extends object, TValue = unknown> = CellContext<DataTableFeatures, T, TValue>;
export type DataTableRow<T extends object> = Row<DataTableFeatures, T>;

export const amountHeader = ({ name, width = '10rem', fontWeight = 'bold', fontSize = '.7rem', casing = 'uppercase', explanation = undefined }:
    { name: string | string[], width?: string, fontWeight?: string, fontSize?: string, casing?: 'uppercase' | 'lowercase' | 'none', explanation?: string | string[] }) => () => (
        <Flex style={{ width: width }} justify='right' alignItems='center'>
            {explanation && <ExplanationPopover explanation={explanation} />}
            <Box>
                {(Array.isArray(name) ? name : [name]).map((line, index) => (
                    <Text key={`${line}-${index}`} textTransform={casing} fontWeight={fontWeight} fontSize={fontSize} style={{ textAlign: 'right', paddingRight: '.1rem' }}>{line}</Text>
                ))}
            </Box>
        </Flex>
    )
export const amountCell = <T extends object>({ width = '10rem', fontWeight = 'normal', showSign = false }: { width?: string, fontWeight?: string, showSign?: boolean }) => (info: DataTableCellContext<T, number>) => {
    const value = info.getValue();
    const text = `${showSign && value > 0 ? '+' : ''}${AmountUtil.format(value)}`;
    return amountText(text, width, fontWeight);
}
export const amountText = (text: string, width: string, fontWeight: string, fontColor?: string) =>
    <Text cursor='default' width={width} fontWeight={fontWeight} color={fontColor} textAlign='right' pr='0rem'>{text}</Text>

export const moneyCell = <T extends object>(width: string = '10rem') => (info: DataTableCellContext<T, Money | null | undefined>) => {
    const money = info.getValue();
    return moneyText(money, width, 'normal');
}
export const moneyText = (money: Money | null | undefined, width: string, fontWeight: string, fontColor?: string) =>
    money
        ? valueCurrencyText(money.value, money.currency, width, fontWeight, fontColor)
        : amountText('-', width, fontWeight, fontColor);
export const valueCurrencyText = (value: number, currency: string, width: string, fontWeight: string, fontColor?: string, showSign = false) =>
    <Text cursor='default' width={width}
        fontWeight={fontWeight} textAlign='right'
        color={fontColor}
        pr='0rem'>{showSign && value > 0 ? '+' : ''}{AmountUtil.format(value)} {currency}</Text>

export const moneyListCell = (moneys: ReadonlyArray<Money>, defaultCurrency: string, fontWeight: string = 'normal', width: string = '10rem') => {
    const text = moneys.length === 0 ? `${AmountUtil.format(0)} ${defaultCurrency}` : moneys.map(m => `${AmountUtil.format(m.value)} ${m.currency}`).join(' ');
    return <Text cursor='default' width={width} textAlign='right' pr='0rem' fontWeight={fontWeight}>{text}</Text>
}

export const moneySort = <T extends object>() => (rowA: DataTableRow<T>, rowB: DataTableRow<T>, columnId: string): number => {
    const a: Money | null | undefined = rowA.getValue(columnId);
    const b: Money | null | undefined = rowB.getValue(columnId);
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.value < b.value ? 1 : a.value > b.value ? -1 : 0;
}
export const dateCell = <T extends object>(fontSize: string = 'none') => (info: DataTableCellContext<T, Date>) => (
    <Text fontSize={fontSize} cursor='default' pr='0rem'>{formatOverview(info.getValue())}</Text>
)

interface DataTableCommonProps<T extends object> {
    columns: DataTableColumnDef<T>[];
    data: T[];
    hideColumnsOnXs?: Array<string>;
    onRowClick?: (row: T, columnId: string) => void;
    tableSize?: 'sm' | 'md' | 'lg';
}

interface DataTableClientProps<T extends object> {
    /** Client mode owns sorting, filtering and optional pagination for the complete data set. */
    mode?: 'client';
    initialSortByColumn?: string | string[];
    initialSortByDesc?: boolean | boolean[];
    initialGroupByColumns?: Array<string>;
    onFilter?: (row: T, value: string) => boolean;
    filter?: string;
    pageSize?: number;
    pagination?: never;
    serverSorting?: never;
}

export interface DataTableServerPagination {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    loading?: boolean;
}

export interface DataTableServerSorting {
    state: SortingState;
    /** Only columns implemented by the API receive a sort control. */
    columnIds: readonly string[];
    onChange: (sorting: SortingState) => void;
}

interface DataTableServerProps {
    /** Server mode treats data as one already-filtered and already-sorted page. */
    mode: 'server';
    pagination: DataTableServerPagination;
    serverSorting?: DataTableServerSorting;
    onFilter?: never;
    filter?: never;
    pageSize?: never;
}

export type DataTableProps<T extends object> = DataTableCommonProps<T> &
    (DataTableClientProps<T> | DataTableServerProps);

export interface DataTableToolbarProps {
    children: ReactNode;
    filtersActive?: boolean;
    onClearFilters?: () => void;
    resultCount?: number;
}

/** Shared placement and language for table filters and their result count. */
export const DataTableToolbar = ({ children, filtersActive = false, onClearFilters, resultCount }: DataTableToolbarProps) => {
    const { t } = useI18n();
    return (
        <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'center' }} justify='space-between'>
            <Flex gap={2} flexWrap='wrap' align='center' role='search' aria-label={t('common.table.filters')}>
                {children}
                {onClearFilters && (
                    <Button size='sm' variant='ghost' onClick={onClearFilters} disabled={!filtersActive}>
                        {t('common.table.clearFilters')}
                    </Button>
                )}
            </Flex>
            {resultCount !== undefined && (
                <Text color='fg.muted' fontSize='sm' whiteSpace='nowrap' aria-live='polite'>
                    {t('common.table.results', { count: resultCount })}
                </Text>
            )}
        </Flex>
    );
};

const initialSortingState = (initialSortByColumn?: string | string[], initialSortByDesc?: boolean | boolean[]): SortingState => {
    const sortByDesc = Array.isArray(initialSortByDesc) ? initialSortByDesc : [initialSortByDesc ?? false];
    if (!initialSortByColumn) return [];
    return (Array.isArray(initialSortByColumn) ? initialSortByColumn : [initialSortByColumn])
        .map((id, index) => ({ id, desc: sortByDesc[index] ?? sortByDesc[0] ?? false }));
};

const useResponsiveColumnVisibility = (hideColumnsOnXs?: Array<string>) => {
    const isSmallScreen = useBreakpointValue({ base: true, md: false });
    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

    useEffect(() => {
        const responsiveVisibility = Object.fromEntries(
            (hideColumnsOnXs ?? []).map(columnId => [columnId, !isSmallScreen]),
        );
        setColumnVisibility(responsiveVisibility);
    }, [hideColumnsOnXs, isSmallScreen]);

    return { columnVisibility, setColumnVisibility };
};

export const DataTable = <T extends object,>(props: PropsWithChildren<DataTableProps<T>>): React.ReactElement<DataTableProps<T>> => {
        const { t } = useI18n();
        const {
            columns,
            data,
            hideColumnsOnXs,
            onRowClick,
            tableSize = 'sm',
        } = props;
        const isServerMode = props.mode === 'server';
        const initialSortByColumn = isServerMode ? undefined : props.initialSortByColumn;
        const initialSortByDesc = isServerMode ? undefined : props.initialSortByDesc;
        const initialGroupByColumns = isServerMode ? undefined : props.initialGroupByColumns;
        const clientFilter = isServerMode ? undefined : props.filter;
        const clientFilterFn = isServerMode ? undefined : props.onFilter;
        const clientPageSize = isServerMode ? undefined : props.pageSize;
        const [clientSorting, setClientSorting] = useState<SortingState>(() => initialSortingState(initialSortByColumn, initialSortByDesc));
        const sorting = isServerMode ? props.serverSorting?.state ?? [] : clientSorting;

        const initialGrouping = initialGroupByColumns ?? [];
        const [grouping, setGrouping] = useState<GroupingState>(initialGrouping);
        const [globalFilter, setGlobalFilter] = useState(clientFilter);
        const { columnVisibility, setColumnVisibility } = useResponsiveColumnVisibility(hideColumnsOnXs);
        const [clientPagination, setClientPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: clientPageSize ?? 10 });
        const clientPaginationEnabled = !!clientPageSize && clientPageSize > 0;
        const previousPageSize = useRef(clientPageSize);
        const previousDataLength = useRef(data.length);
        const previousFilter = useRef(clientFilter);

        const globalFilterFn: FilterFn<DataTableFeatures, T> = (row, _columnId, value, _addMeta) => {
            return clientFilterFn?.(row.original, value) ?? true;
        }

        const tableInstance =
            useTable({
                features: dataTableFeatures,
                columns,
                data,
                state: {
                    sorting,
                    grouping,
                    globalFilter,
                    columnVisibility,
                },
                onGlobalFilterChange: setGlobalFilter,
                globalFilterFn: clientFilterFn && globalFilterFn,
                onSortingChange: updater => {
                    const nextSorting = typeof updater === 'function' ? updater(sorting) : updater;
                    if (isServerMode) {
                        props.serverSorting?.onChange(nextSorting);
                        props.pagination.onPageChange(1);
                    } else {
                        setClientSorting(nextSorting);
                        setClientPagination(current => ({ ...current, pageIndex: 0 }));
                    }
                },
                onGroupingChange: setGrouping,
                onColumnVisibilityChange: setColumnVisibility,
                enableSorting: !isServerMode || !!props.serverSorting,
                manualSorting: isServerMode,
                manualFiltering: isServerMode,
                debugTable: false,
            });

        const stripedRowColor = useColorModeValue('teal.50', 'gray.700');

        // ignore click when is on a link or button
        const handleRowClick = (event: MouseEvent<HTMLElement>, row: T, columnId: string) => {
            const target = event.target as HTMLElement;
            if (!target.closest('a, button, input, select, textarea')) onRowClick?.(row, columnId);
        };
        const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T, columnId: string) => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onRowClick?.(row, columnId);
            }
        };

        useEffect(() => {
            if (initialGroupByColumns) {
                tableInstance.toggleAllRowsExpanded(true);
            }
        }, [initialGroupByColumns]);

        useEffect(() => {
            setGlobalFilter(clientFilter);
        }, [clientFilter]);

        useEffect(() => {
            if (!isServerMode && clientPaginationEnabled) {
                const changed = previousPageSize.current !== clientPageSize || previousDataLength.current !== data.length || previousFilter.current !== clientFilter;
                if (changed) {
                    setClientPagination(current => ({ pageIndex: 0, pageSize: clientPageSize ?? current.pageSize }));
                }
            }
            previousPageSize.current = clientPageSize;
            previousDataLength.current = data.length;
            previousFilter.current = clientFilter;
        }, [clientFilter, clientPageSize, clientPaginationEnabled, data.length, isServerMode]);

        const renderTable = () => {
            const allRows = tableInstance.getRowModel().rows;
            const startIndex = clientPagination.pageIndex * clientPagination.pageSize;
            const rows = !isServerMode && clientPaginationEnabled
                ? allRows.slice(startIndex, startIndex + clientPagination.pageSize)
                : allRows;

            return (
                <Table.Root
                    variant='line'
                    size={tableSize}
                    mt='.5rem'
                    borderY='1px'
                    borderColor='gray.300'
                >
                    <Table.Header position='sticky' top={0}>
                        {tableInstance.getHeaderGroups().map((headerGroup, ix) => (
                            <HeaderRow key={`hg_${headerGroup.id || ix}`} headerGroup={headerGroup} index={ix}
                                initialGroupByColumns={initialGroupByColumns}
                                sortableColumnIds={isServerMode ? props.serverSorting?.columnIds : undefined} />
                        ))}
                    </Table.Header>
                    <Table.Body>
                        {rows.map((row, index) => {
                            const rowIsGrouped = row.getIsGrouped(); // virtual can't be grouped
                            const isEven = index % 2 === 0;
                            return (
                                <Table.Row
                                    key={row.id}
                                    _hover={onRowClick && { bg: 'bg.emphasized' }}
                                    bg={isEven ? stripedRowColor : ''}
                                    shadow={rowIsGrouped ? 'md' : 'none'}
                                    cursor={onRowClick ? 'pointer' : undefined}
                                    tabIndex={onRowClick && !rowIsGrouped ? 0 : undefined}
                                    onKeyDown={onRowClick && !rowIsGrouped
                                        ? event => handleRowKeyDown(event, row.original, row.getVisibleCells()[0]?.column.id ?? '')
                                        : undefined}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <Table.Cell mx={0} px={1}
                                            key={cell.id}
                                            // detect background color
                                            style={{
                                                background: cell.getIsGrouped() || rowIsGrouped
                                                    ? 'var(--chakra-colors-bg-muted)'
                                                    : cell.getIsAggregated()
                                                        ? 'var(--chakra-colors-bg-muted)'
                                                        : cell.getIsPlaceholder()
                                                            ? stripedRowColor // when grouped and aggregated the empty space
                                                            : '', // the regular rows
                                                color: cell.getIsAggregated() ? 'var(--chakra-colors-fg-info)' : ''
                                            }}
                                            onClick={onRowClick && ((ev) => {
                                                // handle click on regular rows only
                                                let enableRowClick = !cell.getIsAggregated() && !cell.getIsGrouped();
                                                if (enableRowClick) {
                                                    // console.info(`column=${JSON.stringify(cell.column.id)}`);
                                                    handleRowClick(ev, row.original, cell.column.id);
                                                }
                                            }
                                            )}
                                        >
                                            {/* {index}-{virtualRow.index} */}
                                            {cell.getIsGrouped() ? (
                                                // If it's a grouped cell, add an expander and row count
                                                <>
                                                    <HStack>
                                                        <Button variant='ghost' size='xs'
                                                            aria-label={t(row.getIsExpanded() ? 'common.table.collapseGroup' : 'common.table.expandGroup')}
                                                            onClick={row.getToggleExpandedHandler()}
                                                            disabled={!row.getCanExpand()}>
                                                            {row.getIsExpanded() ? <BiMinusCircle size={'2em'} /> : <BiPlusCircle size={'2em'} />}
                                                        </Button>{' '}
                                                        <span>
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}{' '}
                                                            ({row.subRows.length})
                                                        </span>
                                                    </HStack>
                                                </>
                                            ) : cell.getIsAggregated() ? (
                                                // If the cell is aggregated, use the Aggregated renderer for cell
                                                flexRender(
                                                    cell.column.columnDef.aggregatedCell ??
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )
                                            ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                                                // Otherwise, just render the regular cell
                                                flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )
                                            )}
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table.Root>

            );
        }

        return (
            <Box mt='0' pt='0' minW='0' maxW='full'>
                <Box overflowX='auto' px='1px'>
                    {renderTable()}
                </Box>
                {isServerMode ? (
                    <TablePagination {...props.pagination} />
                ) : clientPaginationEnabled ? (
                    <TablePagination
                        page={clientPagination.pageIndex + 1}
                        pageSize={clientPagination.pageSize}
                        total={tableInstance.getRowModel().rows.length}
                        onPageChange={page => setClientPagination(current => ({ ...current, pageIndex: page - 1 }))}
                    />
                ) : null}
            </Box>
        );
    };

const HeaderRow = <T extends object>({ headerGroup, index, initialGroupByColumns, applyColumnSizing = false, sortableColumnIds }: {
    headerGroup: HeaderGroup<DataTableFeatures, T>,
    index: number,
    initialGroupByColumns?: Array<string>,
    applyColumnSizing?: boolean,
    sortableColumnIds?: readonly string[]
}) => {
    const { t } = useI18n();
    return (<Table.Row key={`hg_${headerGroup.id || index}`} shadow='md'>
        {headerGroup.headers.map((header, headerIndex) => {
            const canSort = header.column.getCanSort()
                && (!sortableColumnIds || sortableColumnIds.includes(header.column.id));
            const sorted = header.column.getIsSorted();
            const columnLabel = typeof header.column.columnDef.header === 'string'
                ? header.column.columnDef.header
                : header.column.id;
            return (
                <Table.ColumnHeader
                    key={`${header.id}-${headerIndex}`}
                    colSpan={header.colSpan}
                    px={1}
                    py={3}
                    style={applyColumnSizing ? { width: `${header.getSize()}px` } : undefined}
                >
                    {(header.column.getCanGroup() && initialGroupByColumns) ? (
                        <Button variant='ghost' size='xs'
                            aria-label={t(header.column.getIsGrouped() ? 'common.table.ungroup' : 'common.table.group', { column: columnLabel })}
                            onClick={header.column.getToggleGroupingHandler()}>
                            {header.column.getIsGrouped() ? <FcList size='2em' /> : <FcTreeStructure size='2em' />}
                        </Button>
                    ) : null}
                    <Flex alignItems='center'>
                        {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                        )}
                        {canSort ?
                            <Button variant='ghost' size='xs' px={1}
                                aria-label={t(
                                    sorted === 'asc' ? 'common.table.sortDescending'
                                        : sorted === 'desc' ? 'common.table.clearSort'
                                            : 'common.table.sortAscending',
                                    { column: columnLabel },
                                )}
                                onClick={header.column.getToggleSortingHandler()}>
                                {(sorted === 'desc' ? (
                                    <BiSortDown color='var(--chakra-colors-fg-info)' size='1.5em' />
                                ) : sorted === 'asc' ? (
                                    <BiSortUp color='var(--chakra-colors-fg-info)' size='1.5em' />
                                ) : <BiSolidSortAlt color='var(--chakra-colors-fg-muted)' size='1.5em' />
                                )}
                            </Button> : null
                        }
                    </Flex>
                </Table.ColumnHeader>
            )
        })}
    </Table.Row>);
}

export type DataTableVirtualProps<T extends object> = DataTableCommonProps<T> & DataTableClientProps<T>;

export const DataTableVirtual = <T extends object,>({
    columns,
    data,
    initialSortByColumn,
    initialSortByDesc,
    initialGroupByColumns,
    hideColumnsOnXs,
    onRowClick,
    onFilter,
    filter,
    tableSize = 'sm'
}: PropsWithChildren<DataTableVirtualProps<T>>): React.ReactElement<DataTableVirtualProps<T>> => {
        const { t } = useI18n();
        const [sorting, setSorting] = useState<SortingState>(() => initialSortingState(initialSortByColumn, initialSortByDesc));
        const [globalFilter, setGlobalFilter] = useState(filter);
        const { columnVisibility, setColumnVisibility } = useResponsiveColumnVisibility(hideColumnsOnXs);
        const globalFilterFn: FilterFn<DataTableFeatures, T> = (row, _columnId, value) =>
            onFilter?.(row.original, value) ?? true;

        const stripedRowColor = useColorModeValue('teal.50', 'gray.700');

        const table = useTable({
            features: dataTableFeatures,
            data,
            columns,
            state: {
                sorting,
                globalFilter,
                columnVisibility,
            },
            onSortingChange: setSorting,
            onGlobalFilterChange: setGlobalFilter,
            globalFilterFn: onFilter && globalFilterFn,
            onColumnVisibilityChange: setColumnVisibility,
            debugTable: false,
        })

        const { rows } = table.getRowModel();

        const parentRef = useRef<HTMLDivElement>(null);
        const headerRef = useRef<HTMLTableSectionElement>(null);
        const [headerHeight, setHeaderHeight] = useState(0);
        const getRowKey = useCallback((index: number) => rows[index].id, [rows]);

        useEffect(() => {
            const header = headerRef.current;
            if (!header) return;
            const measureHeader = () => setHeaderHeight(header.getBoundingClientRect().height);
            measureHeader();
            const observer = new ResizeObserver(measureHeader);
            observer.observe(header);
            return () => observer.disconnect();
        }, []);

        const virtualizer = useVirtualizer({
            count: rows.length,
            getScrollElement: () => parentRef.current,
            getItemKey: getRowKey,
            estimateSize: () => 34,
            scrollMargin: headerHeight,
            useAnimationFrameWithResizeObserver: true,
            overscan: 10,
        });
        const virtualRows = virtualizer.getVirtualItems();
        const paddingTop = Math.max(0, (virtualRows[0]?.start ?? headerHeight) - headerHeight);
        const paddingBottom = Math.max(0, virtualizer.getTotalSize() - ((virtualRows[virtualRows.length - 1]?.end ?? headerHeight) - headerHeight));
        const hasMoreItems = rows.length > 0 && (virtualizer.scrollOffset ?? 0) + (virtualizer.scrollRect?.height ?? 0)
            < headerHeight + virtualizer.getTotalSize() - 10;

        useEffect(() => {
            setGlobalFilter(filter);
        }, [filter]);

        useEffect(() => {
            virtualizer.scrollToOffset(0);
        }, [sorting, globalFilter]);

        const scrollToEnd = () => {
            if (rows.length > 0) virtualizer.scrollToIndex(rows.length - 1, { align: 'end' });
        };

        return (
            <>
                <Box ref={parentRef} overflow='auto' maxH='700px' minW='0' maxW='full' mt='.5rem' px='1px'>
                    <Table.Root size={tableSize} width={`${table.getTotalSize()}px`} minW='full' tableLayout='fixed'
                        borderY='1px' borderColor='border' aria-rowcount={rows.length + table.getHeaderGroups().length}>
                        <Table.Header ref={headerRef} position='sticky' top='0' zIndex='0' bg='bg.panel'>
                            {table.getHeaderGroups().map((headerGroup, ix) => (
                                <HeaderRow key={`hg_${headerGroup.id || ix}`} headerGroup={headerGroup} index={ix} applyColumnSizing />
                            ))}
                        </Table.Header>
                        <Table.Body>
                            {paddingTop > 0 && <Table.Row aria-hidden='true'>
                                <Table.Cell colSpan={table.getVisibleLeafColumns().length} p='0' borderWidth='0' height={`${paddingTop}px`} />
                            </Table.Row>}
                            {virtualRows.map(virtualRow => {
                                const row = rows[virtualRow.index];
                                return (
                                    <Table.Row key={row.id} ref={virtualizer.measureElement} data-index={virtualRow.index} height='34px'
                                        aria-rowindex={virtualRow.index + table.getHeaderGroups().length + 1}
                                        bg={virtualRow.index % 2 === 0 ? stripedRowColor : ''}
                                        _hover={onRowClick ? { bg: 'bg.emphasized', cursor: 'pointer' } : undefined}
                                        tabIndex={onRowClick ? 0 : undefined}
                                        onClick={event => {
                                            const target = event.target as HTMLElement;
                                            if (onRowClick && !target.closest('a, button, input, select, textarea')) {
                                                onRowClick(row.original, row.getVisibleCells()[0]?.column.id ?? '');
                                            }
                                        }}
                                        onKeyDown={event => {
                                            if (onRowClick && event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                                event.preventDefault();
                                                onRowClick(row.original, row.getVisibleCells()[0]?.column.id ?? '');
                                            }
                                        }}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <Table.Cell key={cell.id} m='0' px='1' py='0' borderWidth='0'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Table.Cell>
                                        ))}
                                    </Table.Row>
                                );
                            })}
                            {paddingBottom > 0 && <Table.Row aria-hidden='true'>
                                <Table.Cell colSpan={table.getVisibleLeafColumns().length} p='0' borderWidth='0' height={`${paddingBottom}px`} />
                            </Table.Row>}
                        </Table.Body>
                    </Table.Root>
                </Box>
                {hasMoreItems && (
                    <Button mt='2' colorPalette='blue' rounded='md' size='sm' onClick={scrollToEnd}>
                        <BiChevronsDown /> {t('common.table.scrollForMore')}
                    </Button>
                )}
            </>
        );
    }

export default DataTable;
